import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, ExternalLink, Pencil, Plus, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { copiarTexto } from './rotina';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Aba Acessos (Adnéia, 17/10/2026, decisões 2 e 3): o portal de cada canal de venda, com
// link e usuário, no lugar da planilha de dados de parceiro. Só o atendimento vê; só a
// gestão edita. A SENHA não entra aqui até o Allan responder o cartão de 17/10.
const AcessosCanais = () => {
  const { getAuthHeader } = useAuth();
  const [rows, setRows] = useState(null);
  const [podeEditar, setPodeEditar] = useState(false);
  const [editando, setEditando] = useState(null);   // canal em edição ('' = canal novo)
  const [form, setForm] = useState({ canal: '', link: '', usuario: '', obs: '' });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/dashboard/acessos`, { headers: getAuthHeader() });
      setRows(r.data.rows || []);
      setPodeEditar(!!r.data.pode_editar);
      setErro('');
    } catch (e) {
      setErro(e?.response?.status === 403 ? 'Esta aba é só do atendimento.' : 'Não carregou os acessos. Tente de novo.');
    }
  }, [getAuthHeader]);
  useEffect(() => { carregar(); }, [carregar]);

  const abrirEdicao = (r) => {
    setEditando(r ? r.canal : '');
    setForm(r ? { canal: r.canal, link: r.link, usuario: r.usuario, obs: r.obs } : { canal: '', link: '', usuario: '', obs: '' });
  };
  const salvar = async () => {
    const canal = (form.canal || '').trim();
    if (!canal) { toast.error('Escreva o nome do canal.'); return; }
    setSalvando(true);
    try {
      await axios.put(`${API_URL}/api/dashboard/acessos/${encodeURIComponent(canal)}`,
        { link: form.link, usuario: form.usuario, obs: form.obs }, { headers: getAuthHeader() });
      toast.success(`Acesso do ${canal} gravado`);
      setEditando(null);
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não gravou. Tente de novo.');
    } finally { setSalvando(false); }
  };
  const copiar = (txt, rotulo) => copiarTexto(txt, () => toast.success(`${rotulo} copiado`), () => toast.error('Não copiou. Selecione e copie com Ctrl+C.'));

  const quando = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  const formLinha = (
    <tr className="border-b bg-slate-50/60 dark:bg-slate-800/20">
      <td className="px-3 py-2">
        {editando ? <span className="font-semibold">{form.canal}</span> : (
          <Input value={form.canal} onChange={e => setForm(f => ({ ...f, canal: e.target.value }))} placeholder="Nome do canal" className="h-8" maxLength={60} />
        )}
      </td>
      <td className="px-3 py-2"><Input value={form.link} onChange={e => setForm(f => ({ ...f, link: e.target.value }))} placeholder="https://…" className="h-8" maxLength={500} /></td>
      <td className="px-3 py-2"><Input value={form.usuario} onChange={e => setForm(f => ({ ...f, usuario: e.target.value }))} placeholder="usuário do portal" className="h-8" maxLength={200} autoComplete="off" /></td>
      <td className="px-3 py-2"><Input value={form.obs} onChange={e => setForm(f => ({ ...f, obs: e.target.value }))} placeholder="observação (sem senha)" className="h-8" maxLength={500} /></td>
      <td className="px-3 py-2 whitespace-nowrap">
        <Button size="sm" onClick={salvar} disabled={salvando}>Gravar</Button>
        <Button size="sm" variant="ghost" onClick={() => setEditando(null)} disabled={salvando}>Cancelar</Button>
      </td>
    </tr>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Acessos aos portais dos canais</CardTitle>
        <CardDescription>
          O link e o usuário de cada portal, no lugar da planilha de dados de parceiro. Só o atendimento vê esta aba.
          <br /><strong>A senha não fica aqui:</strong> isso espera a resposta do Allan. Nunca escreva senha na observação.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {erro && <p className="px-6 pb-4 text-sm text-red-600">{erro}</p>}
        {!erro && rows === null && <p className="px-6 pb-4 text-sm text-muted-foreground flex items-center gap-2"><RefreshCw className="h-4 w-4 animate-spin" /> Carregando…</p>}
        {rows && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/30 border-y">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Canal</th>
                  <th className="text-left px-3 py-2 font-semibold">Portal</th>
                  <th className="text-left px-3 py-2 font-semibold">Usuário</th>
                  <th className="text-left px-3 py-2 font-semibold">Observação</th>
                  <th className="text-left px-3 py-2 font-semibold">{podeEditar ? '' : 'Atualizado'}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => editando === r.canal ? <React.Fragment key={r.canal}>{formLinha}</React.Fragment> : (
                  <tr key={r.canal} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="px-3 py-2 font-medium">{r.canal}</td>
                    <td className="px-3 py-2">
                      {r.link ? (
                        <a href={r.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-300 hover:underline">
                          <ExternalLink className="h-3.5 w-3.5" /> Abrir portal
                        </a>
                      ) : /e-mail/i.test(r.obs || '')
                        ? <span className="text-muted-foreground">por e-mail</span>
                        : <span className="text-muted-foreground">?</span>}
                    </td>
                    <td className="px-3 py-2">
                      {r.usuario ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="font-mono text-sm">{r.usuario}</span>
                          <button type="button" title="Copiar o usuário" onClick={() => copiar(r.usuario, 'Usuário')} className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"><Copy className="h-3.5 w-3.5" /></button>
                        </span>
                      ) : <span className="text-muted-foreground">?</span>}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300">{r.obs}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-muted-foreground">
                      {podeEditar ? (
                        <Button size="sm" variant="ghost" onClick={() => abrirEdicao(r)} title={r.atualizado_por ? `Atualizado por ${r.atualizado_por} em ${quando(r.atualizado_em)}` : 'Ainda sem dado'}>
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                        </Button>
                      ) : (r.atualizado_por ? `${r.atualizado_por} · ${quando(r.atualizado_em)}` : '')}
                    </td>
                  </tr>
                ))}
                {editando === '' && formLinha}
              </tbody>
            </table>
            {podeEditar && editando === null && (
              <div className="px-3 py-3">
                <Button size="sm" variant="outline" onClick={() => abrirEdicao(null)}><Plus className="h-4 w-4 mr-1" /> Outro portal</Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AcessosCanais;
